import { useRef, useState } from 'react';
import { getUploadErrorMessage, postUploadFormDataWithRetry } from '../../lib/uploadRetry';
import { ImagePlus, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import SingleImageUpload from '../ui/SingleImageUpload';

const MAX_IMAGES = 5;
const API_URL = window.__BACKEND_URL__;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
const MAX_FILE_BYTES = 15 * 1024 * 1024;

function fileExtension(file) {
  const name = file?.name || '';
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}

function isAcceptedImage(file) {
  const type = (file?.type || '').toLowerCase();
  const ext = fileExtension(file);
  return ACCEPTED_TYPES.includes(type) || ACCEPTED_EXTENSIONS.includes(ext);
}

function cleanImages(values) {
  return Array.from(
    new Set((Array.isArray(values) ? values : []).filter((value) => typeof value === 'string' && value.trim()))
  ).slice(0, MAX_IMAGES);
}

export default function CastingCallImageUpload({ values = [], onChange }) {
  const images = cleanImages(values);
  const multiInputRef = useRef(null);
  const [batchUploading, setBatchUploading] = useState(false);

  const uploadBatch = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';

    if (!files.length) return;

    const available = MAX_IMAGES - images.length;
    if (available <= 0) {
      toast.error(`You can only upload ${MAX_IMAGES} images.`);
      return;
    }

    if (files.length > available) {
      toast.error(`You can add ${available} more image${available === 1 ? '' : 's'} (maximum ${MAX_IMAGES}).`);
      return;
    }

    for (const file of files) {
      if (!isAcceptedImage(file)) {
        toast.error(`${file.name}: use JPG, PNG, WEBP or HEIC.`);
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        toast.error(`${file.name}: image is larger than 15MB.`);
        return;
      }
    }

    setBatchUploading(true);
    try {
      // Keep the current retry helper, but process this deliberately small
      // batch sequentially. Image optimisation/storage can be expensive and
      // one failed request must not make already-stored successful files look
      // lost to the user.
      const uploaded = [];
      const failed = [];

      for (const file of files) {
        try {
          const form = new FormData();
          form.append('file', file, file.name);
          const { data } = await postUploadFormDataWithRetry(
            `${API_URL}/api/upload/image?kind=flyer`,
            form,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
              withCredentials: true,
            }
          );
          if (!data?.url) throw new Error(`No upload URL returned for ${file.name}`);
          uploaded.push(data.url);
        } catch (error) {
          failed.push({ file, error });
        }
      }

      if (uploaded.length) {
        onChange(cleanImages([...images, ...uploaded]));
      }

      if (failed.length) {
        const firstFailure = getUploadErrorMessage(
          failed[0].error,
          'Could not upload one or more casting images.'
        );
        const successNote = uploaded.length
          ? ` ${uploaded.length} image${uploaded.length === 1 ? '' : 's'} uploaded successfully.`
          : '';
        toast.error(
          `${failed.length} image${failed.length === 1 ? '' : 's'} could not be uploaded.${successNote} ${firstFailure}`.trim()
        );
      } else {
        toast.success(`${uploaded.length} image${uploaded.length === 1 ? '' : 's'} uploaded.`);
      }
    } finally {
      setBatchUploading(false);
    }
  };

  const replaceAt = (index, url) => {
    if (!url) {
      onChange(images.filter((_, imageIndex) => imageIndex !== index));
      return;
    }

    const next = [...images];
    next[index] = url;
    onChange(cleanImages(next));
  };

  const addImage = (url) => {
    if (!url || images.length >= MAX_IMAGES) return;
    onChange(cleanImages([...images, url]));
  };

  return (
    <div className="min-w-0 space-y-4" data-testid="casting-call-image-upload">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-medium text-theme-text">Casting images</p>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-theme-muted">
            Add up to five flyers, artwork or reference images. The first image is used as the main listing image; applicants can open the full gallery.
          </p>
        </div>
        <span className="inline-flex w-fit flex-shrink-0 items-center rounded-full border border-theme bg-theme px-2.5 py-1 text-xs font-medium text-theme-muted">
          {images.length} / {MAX_IMAGES}
        </span>
      </div>

      {images.length < MAX_IMAGES && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={multiInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
            multiple
            className="hidden"
            onChange={uploadBatch}
            data-testid="casting-call-image-multi-input"
          />
          <button
            type="button"
            onClick={() => multiInputRef.current?.click()}
            disabled={batchUploading}
            className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-[#002FA7] px-3 py-2 text-xs font-semibold text-white hover:bg-[#0055FF] disabled:opacity-50"
            data-testid="casting-call-image-multi-select"
          >
            {batchUploading
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Upload className="h-4 w-4" />}
            {batchUploading ? 'Uploading…' : 'Select multiple images'}
          </button>
          <span className="text-[11px] text-theme-muted">
            Select up to {MAX_IMAGES - images.length} at once
          </span>
        </div>
      )}

      <div
        className="grid min-w-0 grid-cols-1 gap-4 min-[430px]:grid-cols-2 lg:grid-cols-3"
        data-testid="casting-call-image-grid"
      >
        {images.map((url, index) => (
          <div
            key={`${url}-${index}`}
            className="relative min-w-0 rounded-2xl border border-theme bg-theme/30 p-3"
            data-testid={`casting-call-image-slot-${index}`}
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-theme-text">
                Image {index + 1}{index === 0 ? ' · Main image' : ''}
              </span>
            </div>
            <SingleImageUpload
              value={url}
              onChange={(nextUrl) => replaceAt(index, nextUrl)}
              label=""
              description=""
              type="flyer"
              hideRecommendation
              className="[&_[data-testid^='single-image-tile']]:max-w-none"
            />
          </div>
        ))}

        {images.length < MAX_IMAGES && (
          <div
            className="min-w-0 rounded-2xl border border-dashed border-[#002FA7]/35 bg-[#002FA7]/5 p-3"
            data-testid="casting-call-add-image-slot"
          >
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-[#002FA7]">
              <ImagePlus className="h-4 w-4" />
              Add image {images.length + 1}
            </div>
            <SingleImageUpload
              value=""
              onChange={addImage}
              label=""
              description=""
              type="flyer"
              hideRecommendation
              className="[&_[data-testid^='single-image-tile']]:max-w-none"
            />
          </div>
        )}
      </div>

      <p className="text-[11px] leading-relaxed text-theme-muted">
        Recommended: 1080 × 1350 px portrait. JPG, PNG, WEBP or HEIC, up to 15MB each. Full artwork is preserved by default so dates, credits and contact text are not cropped out.
      </p>
    </div>
  );
}
